import React from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import MetadataBadge, { MetadataBadgeVariant } from '../../../../shared/components/metadata-badge/MetadataBadge';
import { EnrichedGroupStats } from '../../models/AgencyStatsDerived';

const formatCost = (value: number): string => `$${value.toFixed(2)}`;

const formatDelta = (value: number, percent: number | null): React.ReactNode => {
  if (value === 0 && percent === null) return <span>-</span>;
  const sign = value > 0 ? '+' : '';
  const percentStr = percent !== null ? ` (${sign}${percent}%)` : '';
  const className = value > 0
    ? 'agency-stats-table__delta--positive'
    : value < 0
      ? 'agency-stats-table__delta--negative'
      : '';
  return <span className={className}>{sign}{value}{percentStr}</span>;
};

interface AgencyStatsTableProps {
  enrichedGroups: EnrichedGroupStats[];
  costPerPrompt: number | null;
}

const AgencyStatsTable: React.FC<AgencyStatsTableProps> = ({ enrichedGroups, costPerPrompt }) => {
  const { t } = useTranslation();

  const promptsDeltaTemplate = (row: EnrichedGroupStats) =>
    row.delta ? formatDelta(row.delta.promptsDelta, row.delta.promptsDeltaPercent) : <span>-</span>;

  const projectsDeltaTemplate = (row: EnrichedGroupStats) =>
    row.delta ? formatDelta(row.delta.projectsDelta, row.delta.projectsDeltaPercent) : <span>-</span>;

  const shareTemplate = (row: EnrichedGroupStats) => <span>{row.shareOfTotal}%</span>;

  const usageScoreTemplate = (row: EnrichedGroupStats) => {
    const variantMap: Record<string, MetadataBadgeVariant> = { HIGH: 'success', MEDIUM: 'pending', LOW: 'secondary' };
    const labelMap = { HIGH: t('usageHigh'), MEDIUM: t('usageMedium'), LOW: t('usageLow') };
    return <MetadataBadge variant={variantMap[row.usageScore]} text={labelMap[row.usageScore]} />;
  };

  const statusTemplate = (row: EnrichedGroupStats) => {
    if (!row.statusAlert) return <span>-</span>;
    const variantMap: Record<string, MetadataBadgeVariant> = { HIGH_GROWTH: 'success', SPIKE: 'danger', LOW_ACTIVITY: 'secondary' };
    const labelMap = { HIGH_GROWTH: t('alertHighGrowth'), SPIKE: t('alertSpike'), LOW_ACTIVITY: t('alertLowActivity') };
    return <MetadataBadge variant={variantMap[row.statusAlert]} text={labelMap[row.statusAlert]} />;
  };

  const dailyAvgTemplate = (row: EnrichedGroupStats) => <span>{row.dailyAverage}/d</span>;

  const costTemplate = (row: EnrichedGroupStats) => {
    if (costPerPrompt === null) return <span>-</span>;
    return <span>{formatCost(costPerPrompt * row.activePrompts)}</span>;
  };

  const usageScoreSortValue = (row: EnrichedGroupStats) => {
    const map = { HIGH: 3, MEDIUM: 2, LOW: 1 };
    return map[row.usageScore];
  };

  return (
    <div className="agency-stats-table">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">{t('agencyStatsTableTitle')}</div>
      </div>
      <DataTable
        value={enrichedGroups}
        stripedRows
        scrollable
        removableSort
        tableClassName="table"
        className="table-wrapper"
        emptyMessage=""
      >
        <Column
          field="groupName"
          header={t('agencyStats')}
          headerClassName="table__header"
          sortable
        />
        <Column
          field="projectCount"
          header={t('agencyStatsProjects')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          header={t('agencyStatsProjectsDelta')}
          body={projectsDeltaTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
          sortField="delta.projectsDelta"
        />
        <Column
          field="totalPrompts"
          header={t('agencyStatsPrompts')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          header={t('agencyStatsPromptsDelta')}
          body={promptsDeltaTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
          sortField="delta.promptsDelta"
        />
        <Column
          field="shareOfTotal"
          header={t('agencyStatsShareOfTotal')}
          body={shareTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          field="activePrompts"
          header={t('agencyStatsActive')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          field="newExisting.newPrompts"
          header={t('agencyStatsNewPrompts')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          field="newExisting.existingPrompts"
          header={t('agencyStatsExisting')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          field="newExisting.deleted"
          header={t('agencyStatsDeleted')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          field="dailyAverage"
          header={t('agencyStatsDailyAvg')}
          body={dailyAvgTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
        />
        <Column
          header={t('agencyStatsUsageScore')}
          body={usageScoreTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
          sortable
          sortField="usageScore"
          sortFunction={(e) => {
            const data = [...(e.data as EnrichedGroupStats[])];
            data.sort((a, b) => (usageScoreSortValue(a) - usageScoreSortValue(b)) * (e.order ?? 1));
            return data;
          }}
        />
        <Column
          header={t('agencyStatsStatus')}
          body={statusTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
        />
        <Column
          header={t('agencyStatsEstCost')}
          body={costTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center"
        />
      </DataTable>
    </div>
  );
};

export default AgencyStatsTable;
